import React, { forwardRef, useCallback, useState } from "react";
import { TextInput as RNTextInput } from "react-native";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { Icon } from "../Icon";
import { Spacer } from "../Spacer";
import { BodyText } from "./Text/BodyText";
import { View } from "./View";
import type { ReactNode } from "react";
import type { TextInputProps as RNTextInputProps } from "react-native";

export interface TextInputV2Props extends RNTextInputProps {
  label?: string;
  labelColor?: string;
  disabled?: boolean;
  isError?: boolean;
  textBelowComponent?: React.ReactNode;
  value?: string;
  onChangeText?: (text: string) => void;
  rightComponent?: ReactNode;
  children?: ReactNode;
}

export const TextInputV2 = forwardRef<RNTextInput, TextInputV2Props>(
  (
    {
      label,
      labelColor,
      disabled = false,
      isError = false,
      textBelowComponent,
      value,
      onChangeText,
      rightComponent,
      ...props
    },
    ref,
  ) => {
    const { onFocus, onBlur, style: passThroughStyle, ...otherProps } = props;
    const [isFocused, setIsFocused] = useState(false);
    const isEmpty = !value?.length;
    const isLabelAbove = isFocused || !!value?.length;

    const resolveBackgroundColor = useCallback(() => {
      if (disabled && value) {
        return styleConstantsV2.colorGrey10;
      } else if (disabled) {
        return styleConstantsV2.colorGrey20;
      } else if (isError) {
        return styleConstantsV2.colorErrorBg;
      }

      return styleConstantsV2.colorWhite;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, isError]);

    const resolveBorderColor = useCallback(() => {
      if (disabled && value) {
        return styleConstantsV2.colorBlack;
      } else if (disabled) {
        return styleConstantsV2.colorGrey20;
      } else if (isError) {
        return styleConstantsV2.colorError;
      } else if (isFocused) {
        return styleConstantsV2.colorPrimaryMid;
      } else if (isEmpty) {
        return styleConstantsV2.colorGrey30;
      }

      return styleConstantsV2.colorBlack;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, isError, isFocused, isEmpty]);

    const resolveFontColor = useCallback(() => {
      if (disabled && value) {
        return styleConstantsV2.colorBlack;
      }
      if (disabled) {
        return styleConstantsV2.colorGrey50;
      }

      return styleConstantsV2.colorBlack;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    const resolveLabelColor = useCallback(() => {
      if (disabled && value) {
        return labelColor || styleConstantsV2.colorGrey;
      }
      if (disabled) {
        return styleConstantsV2.colorGrey50;
      } else if (isError) {
        return styleConstantsV2.colorError;
      }

      return labelColor || styleConstantsV2.colorGrey;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, isError, labelColor]);

    return (
      <View>
        <View
          style={{
            pointerEvents: disabled ? "none" : "auto",
            position: "relative",
          }}
        >
          <View
            style={{
              pointerEvents: "none",
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 18,
              justifyContent: "center",
            }}
          >
            <BodyText
              sizeLarge
              weightRegular
              colorGrey
              style={{
                color: resolveLabelColor(),
                pointerEvents: "none",
                transitionDuration: "300ms",
                transitionProperty: "transform",
                transitionDelay: "0ms",
                transitionTimingFunction: "ease-in-out",
                transform: [
                  { translateY: isLabelAbove ? "-90%" : "0%" },
                  { scale: isLabelAbove ? 0.75 : 1 },
                ],
                transformOrigin: "bottom left",
              }}
            >
              {label}
            </BodyText>
          </View>

          <RNTextInput
            ref={ref}
            value={value}
            onChangeText={onChangeText}
            editable={!disabled}
            allowFontScaling={false}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect={false}
            onFocus={(e) => {
              onFocus?.(e);
              setIsFocused(true);
            }}
            onBlur={(e) => {
              onBlur?.(e);
              setIsFocused(false);
            }}
            style={[
              {
                borderWidth: 2,
                borderColor: resolveBorderColor(),
                borderRadius: styleConstantsV2.borderRadius8,
                paddingHorizontal: styleConstantsV2.spacing16,
                paddingTop: 28,
                paddingBottom: 16,
                fontFamily: "DMSans-Regular",
                fontSize: styleConstantsV2.fontSizeBodyLarge,
                color: resolveFontColor(),
                backgroundColor: resolveBackgroundColor(),
                outline: "none",
              },
              passThroughStyle,
            ]}
            {...otherProps}
          />

          <View
            style={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: [{ translateY: -12 }],
            }}
          >
            {isError && !disabled ? (
              <Icon
                name="cross-circle"
                size={24}
                color={styleConstantsV2.colorError}
              />
            ) : rightComponent ? (
              rightComponent
            ) : null}
          </View>
        </View>

        {textBelowComponent && (
          <View>
            <Spacer small />
            <View style={{ paddingLeft: 16 }}>{textBelowComponent}</View>
          </View>
        )}
      </View>
    );
  },
);
